import qs from "qs"
import { waitFor } from "xstate"
import remarkGfm from "remark-gfm"
import ReactMarkdown from "react-markdown"
import { LinkIcon, Database, XIcon } from "lucide-react"
import { Fragment, useMemo, useReducer } from "react"
import { Link, Outlet, useLoaderData, useParams, type LoaderFunctionArgs } from "react-router"

import { FiltersWrapper, type StoredSession } from "@/components/Filters"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"

import { authMachine } from "../machines/Authmachine"
import { handleResponse, fetchAPI } from "../utils"

const sessionKey = (applicationName = "") => `application-$name-sections-filter/${applicationName}`

async function loader({ params }: LoaderFunctionArgs) {
  await waitFor(authMachine, (state) => state.hasTag("Settled"))

  const snapshot = authMachine.getSnapshot()
  if (!snapshot.hasTag("AuthComplete")) return null as never

  const filters = qs.parse(
    sessionStorage.getItem(sessionKey(params.applicationName)) ?? "",
  ) as unknown as Partial<StoredSession>

  try {
    return await fetchAPI<Section[]>(
      `/api/sections/${params.applicationName}${qs.stringify(filters.parsed, { addQueryPrefix: true, encode: false })}`,
    ).then(handleResponse)
  } catch (error) {
    if (error instanceof Response && error.status === 404) {
      return []
    }

    throw error
  }
}

export function ApplicationSections() {
  const params = useParams()

  return (
    <Fragment>
      <FiltersWrapper sessionKey={sessionKey(params.applicationName)}>
        <FiltersWrapper.Input maxLength={20} name="searchCriteria" label="Name" />
        <FiltersWrapper.Checkbox name="includeInactive" label="Include Inactive" />
      </FiltersWrapper>
      <Outlet />
    </Fragment>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()
  const params = useParams() as unknown as PageParams

  const [selectedSectionIdx, setSelectedSectionIdx] = useReducer(
    (state: number | undefined, action: number | undefined) => (state === action ? undefined : action),
    undefined,
  )
  const selectedSection = useMemo(() => {
    if (selectedSectionIdx == null) return

    const section = data[selectedSectionIdx]
    if (section == null) return

    return {
      name: section.name,
      active: section.active,
      content: `${section.introduction?.trim() ?? ""}\n${section.guidance?.trim() ?? ""}`.trim(),
    }
  }, [data, selectedSectionIdx])

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Database className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg text-muted-foreground">No sections found for this application.</p>
          <p className="mt-2 text-sm text-muted-foreground">Try adjusting your search criteria or filters.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-6 *:min-h-0 *:min-w-0">
      {selectedSection != null && (
        <aside className="flex w-full max-w-sm shrink-0 flex-col gap-4 rounded-lg border bg-background p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-foreground">
              {selectedSection.name.replace(/([a-z])([A-Z])/g, "$1 $2")}
            </h2>
            <Button size="sm" variant="ghost" onClick={() => setSelectedSectionIdx(undefined)}>
              <XIcon />
            </Button>
          </div>
          <p className="flex items-center gap-2 text-sm text-foreground">
            <span className="font-semibold">Status:</span>
            <span className="font-medium">{selectedSection.active ? "Active" : "Inactive"}</span>
          </p>
          <hr />
          <div className="prose prose-sm min-w-fit grow overflow-y-auto text-pretty dark:prose-invert">
            {selectedSection.content === "" ? (
              <p className="grid size-full place-items-center text-center text-muted-foreground">
                No additional content available for this section.
              </p>
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedSection.content}</ReactMarkdown>
            )}
          </div>
          <hr />
          <Link
            className={buttonVariants()}
            to={`/applications/${params.applicationName}/${selectedSection.name.toLowerCase().replace(/\s/g, "-")}`}
          >
            <LinkIcon />
            Open Section
          </Link>
        </aside>
      )}
      <div className="grid flex-auto auto-rows-max grid-cols-[repeat(auto-fit,minmax(min(100%,30rem),1fr))] gap-4 overflow-y-auto">
        {data.map((section, idx) => {
          const isSelected = selectedSection?.name === section.name

          return (
            <div
              key={section.name}
              data-selected={isSelected || undefined}
              onClick={(e) => {
                setSelectedSectionIdx(idx)
                e.currentTarget.scrollIntoView({ behavior: "smooth", block: "start" })
              }}
              className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border data-[selected]:border-info data-[selected]:bg-info/10"
            >
              <div className="flex items-center justify-between bg-primary px-4 py-4 text-primary-foreground group-data-[selected]:bg-info">
                <h3 className="text-center text-base leading-tight font-semibold">
                  {section.name.replace(/([a-z])([A-Z])/g, "$1 $2")}
                </h3>
                {section.isDeleted && <Badge variant="secondary">Deleted</Badge>}
              </div>
              <div className="flex grow flex-col gap-y-4 py-4 *:px-5 *:first:grow">
                {section.introduction == null || section.introduction === "" ? (
                  <p className="mx-auto grid place-items-center text-center text-muted-foreground">
                    No additional content available for this section.
                  </p>
                ) : (
                  <div className="prose prose-sm text-pretty dark:prose-invert">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.introduction}</ReactMarkdown>
                  </div>
                )}
                <hr />
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-2 text-sm text-foreground">
                    <span className="font-semibold">Status:</span>
                    <span className="font-medium">{section.active ? "Active" : "Inactive"}</span>
                  </p>
                  <Link
                    onClick={(e) => e.stopPropagation()}
                    className={buttonVariants({ size: "sm", variant: "outline" })}
                    to={`/applications/${params.applicationName}/${section.name.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    Open
                  </Link>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

ApplicationSections.loader = loader
ApplicationSections.Main = Main

export default ApplicationSections

interface PageParams {
  applicationName: string
}

interface Section {
  name: string
  active: boolean
  application: string
  isDeleted: boolean
  scope: string
  guidance?: string | null
  introduction?: string | null
}
